import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTerritory } from '../contexts/TerritoryContext';
import { useSiteLogo } from '../contexts/SiteLogoContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Menu, X, MapPin, User, LogOut, LayoutDashboard, Globe, ChevronDown, Moon, Sun, Compass, Map, Heart } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import LevelAvatar from './engagement/LevelAvatar';

const localeFlags = { es: '🇨🇱', en: '🇬🇧', pt: '🇧🇷' };

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isProvider, isReferrer } = useAuth();
  const { locale, changeLocale, t } = useI18n();
  const { translateText } = useTranslate();
  const { currency, changeCurrency, currencies, flags } = useCurrency();
  const { selectedCommune, selectedRegion } = useTerritory();
  const { logoUrl, siteName, getFullLogoUrl, darkLogoUrl, getFullDarkLogoUrl } = useSiteLogo();
  const { isDark, rawDark, toggleTheme, isPublicPage } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const displayLogoUrl = isDark && darkLogoUrl ? getFullDarkLogoUrl() : getFullLogoUrl();

  return (
    <nav className={`sticky top-0 z-[9999] glass border-b ${isDark ? 'night-nav' : 'border-gray-100'}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-3 md:px-8">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo - fixed width so it doesn't get squeezed */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            {displayLogoUrl ? (
              <img 
                src={displayLogoUrl} 
                alt={siteName} 
                className="h-8 md:h-10 max-w-[140px] md:max-w-[200px] object-contain"
              />
            ) : (
              <span className={`font-bold text-lg md:text-xl ${isDark ? 'text-white' : 'text-[#064E3B]'}`}>
                {siteName}
              </span>
            )}
          </Link>

          {/* Mobile: Explorar + Mapa links always visible */}
          <div className="flex md:hidden items-center gap-1">
            <Link
              to="/explore"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-[#00CC66]/10 text-[#00CC66] hover:bg-[#00CC66]/20 transition-colors"
              data-testid="nav-explore-mobile"
            >
              <Compass className="w-3.5 h-3.5" />
              {t('explore')}
            </Link>
            <Link
              to="/map"
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isActive('/map') 
                  ? 'bg-[#00CC66]/10 text-[#00CC66]' 
                  : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-[#00CC66]'
              }`}
              data-testid="nav-map-mobile"
            >
              <Map className="w-3.5 h-3.5" />
              {t('map')}
            </Link>
          </div>

          {/* Desktop: Territory indicator */}
          {selectedCommune && (
            <Link
              to="/"
              className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                isDark ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-green-50 text-[#064E3B] hover:bg-green-100'
              }`}
              data-testid="territory-indicator"
            >
              <MapPin className="w-4 h-4 text-[#00CC66]" />
              <span>{selectedCommune.name}, {selectedRegion?.name}</span>
            </Link>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/explore"
              className="text-sm font-semibold !text-[#00CC66] hover:!text-[#7CFFB1] transition-colors"
              data-testid="nav-explore"
            >
              {t('explore')}
            </Link>
            <Link
              to="/map"
              className={`text-sm font-medium transition-colors ${
                isActive('/map') ? 'text-[#00CC66]' : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-[#00CC66]'
              }`}
              data-testid="nav-map"
            >
              {t('map')}
            </Link>
            <Link
              to="/contacto"
              className={`text-sm font-medium transition-colors ${
                isActive('/contacto') ? 'text-[#00CC66]' : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-[#00CC66]'
              }`}
              data-testid="nav-contact"
            >
              {translateText('Contacto')}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Theme toggle - only on public pages */}
            {isPublicPage && (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full w-8 h-8 md:w-9 md:h-9 theme-toggle"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {rawDark ? <Sun className="w-4 h-4 md:w-5 md:h-5 text-yellow-400" /> : <Moon className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />}
              </Button>
            )}

            {/* Language selector - desktop only */}
            <div className="hidden md:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`rounded-full gap-2 ${isDark ? 'text-white hover:bg-white/10' : ''}`} data-testid="locale-selector">
                    <span className="text-sm">{localeFlags[locale]}</span>
                    <Globe className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-gray-500'}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => changeLocale('es')}>🇨🇱 Espanol</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLocale('en')}>🇬🇧 English</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLocale('pt')}>🇧🇷 Portugues</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Currency selector - desktop only */}
            <div className="hidden lg:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`rounded-full ${isDark ? 'text-white hover:bg-white/10' : ''}`} data-testid="currency-selector">
                    <span className="text-sm font-medium">{currency}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {currencies.map((curr) => (
                    <DropdownMenuItem key={curr} onClick={() => changeCurrency(curr)}>
                      {flags[curr]} {curr}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* User menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full gap-1 px-1 md:gap-2 md:px-2 hover:bg-white/10 dark:hover:bg-white/10" data-testid="user-menu">
                    <LevelAvatar
                      name={user.name}
                      color={user.avatar_color}
                      avatarUrl={user.avatar_url}
                      level={user.level || 1}
                      size="md"
                    />
                    <ChevronDown className="w-3 h-3 md:w-4 md:h-4 text-gray-500 hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-3 py-2 flex items-center gap-3">
                    <LevelAvatar
                      name={user.name}
                      color={user.avatar_color}
                      avatarUrl={user.avatar_url}
                      level={user.level || 1}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{user.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                      {user.username && (
                        <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                          {user.level_name || `Nivel ${user.level || 1}`} · {user.points || 0} pts
                        </p>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/me')} data-testid="nav-me">
                    <Heart className="w-4 h-4 mr-2" />
                    Mi panel
                  </DropdownMenuItem>
                  {isProvider && (
                    <DropdownMenuItem onClick={() => navigate('/provider')}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {t('dashboard')}
                    </DropdownMenuItem>
                  )}
                  {isReferrer && (
                    <DropdownMenuItem onClick={() => navigate('/referrer')}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Mi Panel
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <User className="w-4 h-4 mr-2" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/para-proveedores" className="hidden md:block">
                  <Button
                    variant="outline"
                    className={`rounded-full text-sm font-semibold px-4 py-2 ${
                      isDark
                        ? 'bg-transparent border-white/30 text-white hover:bg-white/10 hover:border-white/60'
                        : 'bg-transparent border-gray-300 text-gray-800 hover:bg-gray-50'
                    }`}
                    data-testid="publish-event-btn"
                  >
                    {translateText('Publica tu evento')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    className="rounded-full text-xs md:text-sm font-bold px-3 md:px-5 bg-[#00CC66] hover:bg-[#00b85b] text-neutral-900 shadow-lg shadow-[#00CC66]/20"
                    data-testid="login-btn"
                  >
                    <span className="hidden sm:inline">{t('login')}</span>
                    <span className="sm:hidden">{translateText('Entrar')}</span>
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile menu button - for settings */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden w-8 h-8"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Mobile dropdown: settings, territory, register */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-3 border-t animate-slideUp ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
            <div className="flex flex-col gap-1">
              {selectedCommune && (
                <Link
                  to="/"
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isDark ? 'text-white' : 'text-[#064E3B]'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <MapPin className="w-4 h-4 text-[#00CC66]" />
                  {selectedCommune.name}, {selectedRegion?.name}
                </Link>
              )}
              {/* Language & currency in mobile menu */}
              <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <Globe className="w-4 h-4" />
                <button onClick={() => { changeLocale('es'); setMobileMenuOpen(false); }} className={`text-sm ${locale === 'es' ? 'font-bold text-[#00CC66]' : ''}`}>ES</button>
                <button onClick={() => { changeLocale('en'); setMobileMenuOpen(false); }} className={`text-sm ${locale === 'en' ? 'font-bold text-[#00CC66]' : ''}`}>EN</button>
                <button onClick={() => { changeLocale('pt'); setMobileMenuOpen(false); }} className={`text-sm ${locale === 'pt' ? 'font-bold text-[#00CC66]' : ''}`}>PT</button>
                <span className="text-gray-300 mx-1">|</span>
                {currencies.map((curr) => (
                  <button key={curr} onClick={() => { changeCurrency(curr); setMobileMenuOpen(false); }} className={`text-sm ${currency === curr ? 'font-bold text-[#00CC66]' : ''}`}>{curr}</button>
                ))}
              </div>
              {!user && (
                <Link
                  to="/register"
                  className="mx-3 mt-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="btn-primary w-full text-sm" data-testid="register-btn-mobile">
                    {t('register')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
